import { useTranslation } from 'react-i18next';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

export default function AboutPage() {
  const { t, i18n } = useTranslation();

  useSEO({
    title: 'Hakkımızda | Canel Elektrik – 30+ Yıllık Endüstriyel Mühendislik Deneyimi',
    description:
      "1996'dan bu yana İstanbul Perpa'da faaliyet gösteren Canel Elektrik, ilaç, savunma sanayi ve endüstriyel tesis sektörlerinde 1.250.000+ m² tamamlanmış proje alanıyla Türkiye'nin güvenilir EPC ortağıdır.",
    keywords:
      'Canel Elektrik hakkında, EPC firması İstanbul, endüstriyel mühendislik tarihi, Perpa elektrik taahhüt',
    canonical: '/about',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'Hakkımızda – Canel Elektrik İnşaat Taahhüt Mühendislik',
      url: `${SITE_URL}/about`,
      description:
        "1996 yılında İstanbul'da kurulan Canel Elektrik, 30 yılı aşkın deneyimiyle Türkiye'nin önde gelen EPC firmalarından biridir.",
      mainEntity: {
        '@type': 'Organization',
        name: 'Canel Elektrik İnşaat Taahhüt Mühendislik',
        foundingDate: '1996',
        foundingLocation: 'İstanbul, Türkiye',
        numberOfEmployees: { '@type': 'QuantitativeValue', value: 50 },
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Perpa Ticaret Merkezi, A Blok Kat: 5 No: 204',
          addressLocality: 'Şişli',
          addressRegion: 'İstanbul',
          addressCountry: 'TR',
        },
      },
    },
  });

  const storyImage = i18n.language === 'tr'
    ? 'https://can-el.com/common/img/canel-istanbul-harita-tr.jpg'
    : 'https://can-el.com/common/img/canel-istanbul-harita-en.jpg';

  const timelineMapImage = i18n.language === 'tr'
    ? 'https://can-el.com/common/img/canel-tr-harita-tr.jpg'
    : 'https://can-el.com/common/img/canel-tr-harita-en.jpg';

  const timeline = [
    { year: '1996', titleKey: 'tl_1996_title', descKey: 'tl_1996_desc' },
    { year: '2007', titleKey: 'tl_2007_title', descKey: 'tl_2007_desc' },
    { year: '2008', titleKey: 'tl_2008_title', descKey: 'tl_2008_desc' },
    { year: '2018', titleKey: 'tl_2018_title', descKey: 'tl_2018_desc' },
    { year: '2022', titleKey: 'tl_2022_title', descKey: 'tl_2022_desc' },
    { year: '2026+', titleKey: 'tl_2026_title', descKey: 'tl_2026_desc' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative h-72 md:h-96 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=professional%20engineering%20team%20meeting%20industrial%20facility%20Turkey%20electrical%20construction%20project%20management%20modern%20office&width=1920&height=600&seq=about-hero&orientation=landscape"
          alt="About"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A2332]/80 to-[#1A2332]/40" />
        <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-16 max-w-7xl mx-auto w-full">
          <span className="text-[#E8A020] text-xs font-bold tracking-widest uppercase mb-3">{t('about_label')}</span>
          <h1 className="text-3xl md:text-5xl font-black text-white leading-tight">{t('about_title')}</h1>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-[#E8A020] text-xs font-bold tracking-widest uppercase">{t('about_story_label')}</span>
            <h2 className="text-3xl font-black text-[#1A2332] mt-3 mb-6">{t('about_story_title')}</h2>
            <p className="text-gray-500 text-base leading-relaxed mb-5">{t('about_p1')}</p>
            <p className="text-gray-500 text-base leading-relaxed mb-8">{t('about_p2')}</p>
            <div className="grid grid-cols-2 gap-4">
              {[
                { val: '30+', label: t('stats_years') },
                { val: '1.250.000+ m²', label: t('stats_area') },
                { val: '150+', label: t('stats_projects') },
                { val: '50+', label: t('stats_engineers') },
              ].map((s) => (
                <div key={s.label} className="bg-[#F8F9FA] rounded-lg p-4 text-center">
                  <div className="text-2xl font-black text-[#E8A020]">{s.val}</div>
                  <div className="text-xs text-gray-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="w-full h-[260px] sm:h-[340px] md:h-[420px] rounded-lg overflow-hidden">
            <img
              src={storyImage}
              alt="Our Story"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-[#E8A020] text-xs font-bold tracking-widest uppercase">{t('about_timeline_label')}</span>
            <h2 className="text-3xl font-black text-[#1A2332] mt-3">{t('about_timeline_title')}</h2>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-gray-200 hidden md:block" />
            <div className="flex flex-col gap-10">
              {timeline.map((item, idx) => (
                <div key={item.year} className={`flex flex-col md:flex-row items-center gap-6 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-white rounded-lg p-6 border border-gray-100 inline-block w-full md:max-w-sm">
                      <div className="text-[#E8A020] font-black text-lg mb-1">{item.year}</div>
                      <div className="font-bold text-[#1A2332] text-base mb-1">{t(item.titleKey)}</div>
                      <div className="text-gray-500 text-sm">{t(item.descKey)}</div>
                    </div>
                  </div>
                  <div className="w-4 h-4 rounded-full bg-[#E8A020] border-4 border-white z-10 flex-shrink-0 hidden md:block" />
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Map */}
      <section className="pb-20 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-0">
          <div className="text-center mb-10">
            <span className="text-[#E8A020] text-xs font-bold tracking-widest uppercase">{t('about_refprojects_label')}</span>
            <h2 className="text-3xl font-black text-[#1A2332] mt-3">{t('about_refprojects_title')}</h2>
          </div>
          <img
            src={timelineMapImage}
            alt="Can-El Türkiye Haritası"
            className="w-full rounded-lg"
          />
        </div>
      </section>

      <Footer />
    </div>
  );
}
